/**
 * @file LiveCheckPanel.jsx
 * @description The live commissioning ladder for one TCU — the panel an
 *              installer (or bench tester) watches while wiring:
 *
 *                power → GPS → ignition → CAN → engine
 *
 *              Each step turns green as the corresponding signal arrives in
 *              the device's frames (server: /api/hardware/live-check, polled
 *              every 5s). A stuck step shows what to look at (power/SIM,
 *              antenna, key line, CAN tap / CAN mode). Below the ladder:
 *              the live meters (engine hours, fuel, supply voltage) and the
 *              latest frame's CAN keys, so "is the website seeing what the
 *              machine display shows" is answered on the spot.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Card, Typography, Chip, Stack, CircularProgress, Alert, Link as MuiLink,
} from '@mui/material';
import {
    Power as PowerIcon,
    GpsFixed as GpsIcon,
    Key as KeyIcon,
    SettingsInputComponent as CanIcon,
    Bolt as EngineIcon,
    CheckCircle as OkIcon,
    RadioButtonUnchecked as PendingIcon,
} from '@mui/icons-material';
import hardwareLifecycleService from '../../../services/hardwareLifecycleService';
import { formatToIST } from '../../../utils/dataUtils';

const POLL_MS = 5000;

const STEPS = [
    { key: 'connected', label: 'Power · Device online', icon: <PowerIcon /> },
    { key: 'gps', label: 'GPS fix', icon: <GpsIcon /> },
    { key: 'ignition', label: 'Ignition sensed', icon: <KeyIcon /> },
    { key: 'can', label: 'CAN data arriving', icon: <CanIcon /> },
    { key: 'engine', label: 'Engine running seen', icon: <EngineIcon /> },
];

const fmtT = (ts) => {
    if (!ts) return '';
    const out = formatToIST(ts, 'HH:mm:ss');
    return out === 'N/A' || out === 'Invalid Date' ? '' : out;
};

/**
 * @param {string}   deviceId   the TCU to watch
 * @param {function} onData     optional — receives each live-check response
 *                              (Installation uses it for the meter comparison)
 */
const LiveCheckPanel = ({ deviceId, onData }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        if (!deviceId) return;
        try {
            const r = await hardwareLifecycleService.liveCheck(deviceId);
            setData(r);
            setError('');
            if (onData) onData(r);
        } catch (e) {
            setError(e.message || 'Live check failed');
        } finally {
            setLoading(false);
        }
    }, [deviceId, onData]);

    useEffect(() => {
        setLoading(true);
        setData(null);
        load();
        const id = setInterval(load, POLL_MS);
        return () => clearInterval(id);
    }, [load]);

    if (!deviceId) return null;
    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={26} /></Box>;
    }
    if (error) return <Alert severity="error">{error}</Alert>;

    const checks = data?.checks || {};
    const meters = data?.meters || {};
    const latest = data?.latest_frame;
    const greens = STEPS.filter(s => checks[s.key]?.ok).length;

    return (
        <Box>
            {!data?.registered && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                    This device id is not in the database — frames still show here, but register it before installing.
                </Alert>
            )}

            {/* The ladder */}
            <Card variant="outlined" sx={{ borderRadius: '14px', p: 2, mb: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={800}>
                        Live checks — {deviceId}
                    </Typography>
                    <Chip size="small" label={`${greens}/${STEPS.length} green · refreshes 5s`}
                        color={greens === STEPS.length ? 'success' : 'default'}
                        sx={{ fontWeight: 700, height: 22 }} />
                </Stack>
                <Stack spacing={1.25}>
                    {STEPS.map(step => {
                        const c = checks[step.key] || {};
                        return (
                            <Box key={step.key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                                {c.ok
                                    ? <OkIcon sx={{ color: '#16a34a', fontSize: 20, mt: '1px' }} />
                                    : <PendingIcon sx={{ color: '#cbd5e1', fontSize: 20, mt: '1px' }} />}
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        {React.cloneElement(step.icon, { sx: { fontSize: 15, color: c.ok ? '#16a34a' : 'text.disabled' } })}
                                        <Typography variant="body2" fontWeight={700}
                                            sx={{ color: c.ok ? 'text.primary' : 'text.secondary' }}>
                                            {step.label}
                                        </Typography>
                                        {c.ok && c.at && (
                                            <Typography variant="caption" color="text.disabled">{fmtT(c.at)}</Typography>
                                        )}
                                    </Stack>
                                    <Typography variant="caption" sx={{ color: c.ok ? 'text.secondary' : '#b45309' }}>
                                        {c.ok ? (c.value || 'OK') : (c.hint || 'Waiting…')}
                                    </Typography>
                                    {step.key === 'can' && c.ok && c.sample_keys?.length > 0 && (
                                        <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            {c.sample_keys.map(k => (
                                                <Chip key={k} size="small" label={k}
                                                    sx={{ height: 18, fontSize: 10, fontFamily: 'monospace' }} />
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Stack>
                {!checks.can?.ok && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                        CAN mode and RAW filters are set from{' '}
                        <MuiLink href={`/device-config`} underline="hover">Device Config</MuiLink>
                        {' '}(SHOW CAN CFG · SET CANM RAW|J1939).
                    </Typography>
                )}
            </Card>

            {/* The meters the installer compares with the machine display */}
            <Card variant="outlined" sx={{ borderRadius: '14px', p: 2, mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
                    Telemetry meters (compare with the machine display)
                </Typography>
                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                    {[
                        { label: 'Engine hours', m: meters.engine_hours, unit: 'h' },
                        { label: 'Fuel', m: meters.fuel_pct, unit: '%' },
                        { label: 'Supply voltage', m: meters.voltage, unit: 'V' },
                    ].map(({ label, m, unit }) => (
                        <Box key={label}>
                            <Typography variant="caption" color="text.secondary" fontWeight={700}
                                sx={{ textTransform: 'uppercase', fontSize: 10 }}>
                                {label}
                            </Typography>
                            <Typography variant="h6" fontWeight={800} lineHeight={1.1}>
                                {m ? `${m.value} ${unit}` : '—'}
                            </Typography>
                            {m?.at && <Typography variant="caption" color="text.disabled">{fmtT(m.at)}</Typography>}
                        </Box>
                    ))}
                </Stack>
            </Card>

            {/* Latest frame — proof of exactly what the website is receiving */}
            <Card variant="outlined" sx={{ borderRadius: '14px', p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={800}>Latest frame</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {latest ? `${fmtT(latest.recorded_at)} · ${latest.keys.length} key(s)` : `nothing in ${data?.window_min || 15} min`}
                    </Typography>
                </Stack>
                {latest ? (
                    <Box component="pre" sx={{
                        m: 0, p: 1.25, bgcolor: '#0f172a', color: '#e2e8f0', borderRadius: '10px',
                        fontSize: 11, fontFamily: 'monospace', maxHeight: 200, overflow: 'auto',
                    }}>
                        {JSON.stringify({ location: latest.location, payload: latest.payload }, null, 2)}
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        No frame in the last {data?.window_min || 15} minutes — power the device and wait for it to join the network.
                    </Typography>
                )}
            </Card>
        </Box>
    );
};

export default LiveCheckPanel;
